import { action, computed, observable } from 'mobx';

import { ProgressTab } from '@/components/ProgressTabs';
import { ConsentValidationErrors, ErrorMessages, validateConsent } from '@/pages/avatar/api/';
import { AvatarCreateTab, AvatarType, Consent, FootageMethod, GreenscreenParams, RequestStatus } from '@/pages/avatar/common/types';

import { SupportedLanguage } from '@/utils/types';
import { FootageRequirement, requirementsMap } from '../components/AvatarFootage/ReviewFootage/requirements';
import { handleFootage } from './api';

const pageTabs: ProgressTab[] = [
    { id: AvatarCreateTab.INSTRUCTIONS, label: 'Instructions' },
    { id: AvatarCreateTab.FOOTAGE, label: 'Submit footage' },
    { id: AvatarCreateTab.CONSENT, label: 'Consent' },
    { id: AvatarCreateTab.WAIT_FOR_UPLOADS, label: '', hidden: true },
];

interface InitialValues {
    id?: string;
    footageUrl?: string;
    consent?: Consent | null;
    username?: string;
    thumbnail_url?: string;
    skipConsent?: boolean;
    isGreenScreen?: boolean;
}

export class PremiumPlusCreateAvatarStore {
    type = AvatarType.PremiumPlus;

    @observable accessor id: string | undefined = undefined;

    @observable accessor footageUrl: string | undefined = undefined;
    // File object that contains the footage. If the footage is uploaded, the URL will stored in @footageUrl
    @observable accessor footageFile: File | null = null;
    // Status of the footage upload process
    @observable accessor footageUploadStatus: RequestStatus = RequestStatus.IDLE;
    @observable accessor footageAbortController: AbortController | null = null;
    @observable accessor footageUploadTime: Date | null = null;
    // The consent object that the user has selected
    @observable accessor selectedConsent: Consent | null = null;
    // File object that contains the recorded consent. If the consent is uploaded, the URL will stored in @consentFileSrc
    @observable accessor consentFile: File | null = null;
    // URL of the recorded consent
    @observable accessor consentFileSrc: string = '';
    // Script for the user to read of the given consent
    @observable accessor consentScript: string = '';
    // Username of consent user
    @observable accessor username: string = '';
    // ID of the uploaded consent
    @observable accessor uploadedConsentId: string = '';
    // Status of the consent validation process
    @observable accessor consentValidationStatus: RequestStatus = RequestStatus.IDLE;
    @observable accessor consentAbortController: AbortController | null = null;
    @observable accessor consentValidationErrorMessage: string = '';
    @observable accessor isConsentAgreed: boolean = false;
    @observable accessor tab = AvatarCreateTab.INSTRUCTIONS;
    // Initial values of the avatar in case of editing a draft
    @observable accessor initialValues: InitialValues | null = null;

    @observable accessor requirementsMap = requirementsMap;

    @observable accessor thumbnail_url = '';
    @observable accessor upgradeMinutesPopupOpen: boolean = false;

    // data for mixpanel only
    @observable accessor footageMethod: FootageMethod | null = null;
    @observable accessor usedFootageScript: boolean = false;
    @observable accessor footageScriptLanguage: string = '';
    @observable accessor consentLanguage: SupportedLanguage = 'english';
    @observable accessor isGreenScreen: boolean = false;
    @observable accessor greenscreen: GreenscreenParams | undefined = undefined;
    @observable accessor skipConsent: boolean = false;
    @observable accessor idleVideo: string | undefined = undefined;
    @observable accessor idleVideoUploadStatus: RequestStatus = RequestStatus.IDLE;

    @computed
    get tabs() {
        return pageTabs.map(tab => ({
            ...tab,
            disabled: tab.id === AvatarCreateTab.CONSENT && !this.isFootageConfirmed,
        }));
    }

    @computed
    get payload() {
        const greenscreen = !(this.isGreenScreen && this.greenscreen)
            ? undefined
            : {
                  chroma_key: this.greenscreen.chroma_key,
                  similarity: this.greenscreen.similarity,
                  blend: this.greenscreen.blend,
              };

        if (this.skipConsent) {
            return {
                id: this.id,
                source_url: this.footageUrl,
                name: this.username,
                thumbnail_url: this.thumbnail_url,
                is_greenscreen: this.isGreenScreen,
                greenscreen,
                idle_video: this.idleVideo,
            };
        }

        const consentId =
            this.selectedConsent?.id || (this.consentValidationStatus === RequestStatus.SUCCESS ? this.uploadedConsentId : undefined);

        return {
            id: this.id,
            source_url: this.footageUrl,
            consent_id: consentId,
            thumbnail_url: this.thumbnail_url,
            is_greenscreen: this.isGreenScreen,
            greenscreen,
            idle_video: this.idleVideo,
        };
    }

    @computed
    get isDirty(): boolean {
        const footageInProgress = this.footageUploadStatus === RequestStatus.LOADING || (this.footageFile && !this.isFootageConfirmed);
        const consentInProgress = !this.skipConsent && this.consentValidationStatus === RequestStatus.LOADING;

        if (this.initialValues) {
            const initialValuesChanged =
                this.payload.source_url !== this.initialValues.footageUrl ||
                (!this.skipConsent && this.payload.consent_id !== this.initialValues.consent?.id);

            return initialValuesChanged || footageInProgress || consentInProgress;
        }

        return (
            !!(this.footageUrl || (!this.skipConsent && !!this.selectedConsent) || this.consentFileSrc) ||
            footageInProgress ||
            consentInProgress
        );
    }

    @computed
    get isConsentValid() {
        if (this.consentValidationStatus === RequestStatus.ERROR) return false;

        if (!this.usernameIsValid) return false;

        if (this.skipConsent) {
            return this.isConsentAgreed;
        }

        return !!this.consentFile || !!this.selectedConsent;
    }

    @computed
    get isFootageValid() {
        if (this.footageUploadStatus === RequestStatus.ERROR) return false;

        return this.footageFile || this.footageUrl;
    }

    @computed
    get isValid() {
        return this.isFootageValid && this.isConsentValid;
    }

    @computed
    get usernameIsValid() {
        return !!this.username;
    }

    @computed
    get footageRequirements() {
        return Object.values(this.requirementsMap);
    }

    @computed
    get isFootageConfirmed() {
        return this.footageRequirements.every(
            requirement => requirement.confirmed || (!!requirement.avatarType && requirement.avatarType !== this.type)
        );
    }

    @computed
    get isFootageProvided() {
        return !!this.footageUrl || !!this.footageFile;
    }

    @computed
    get footageSrc() {
        if (this.footageFile) {
            const mimeType = this.footageFile.type === 'video/quicktime' ? 'video/mp4' : this.footageFile.type || 'video/mp4';
            const blob = new Blob([this.footageFile], { type: mimeType });
            return URL.createObjectURL(blob);
        }

        return this.footageUrl;
    }

    @computed
    get consentVideoProps() {
        if (this.selectedConsent) {
            let sourceUrl = '';

            if (this.selectedConsent.video_url) {
                sourceUrl = this.selectedConsent.video_url;
            } else if (this.selectedConsent.status === 'error' && this.consentFile) {
                sourceUrl = URL.createObjectURL(this.consentFile);
            }

            return {
                sourceUrl,
                retryText: 'Redo',
                thumbnailUrl: this.selectedConsent.thumbnail_url,
            };
        }

        if (this.consentFile) {
            return {
                sourceUrl: URL.createObjectURL(this.consentFile!),
                retryText: 'Restart recording',
                fileType: this.consentFile?.type,
            };
        }
    }

    @computed
    get footageReady() {
        return this.footageUploadStatus === RequestStatus.SUCCESS;
    }

    @computed
    get consentReady() {
        return this.consentValidationStatus === RequestStatus.SUCCESS || (this.skipConsent && this.isConsentAgreed);
    }

    @action
    init = ({ id, footageUrl = '', consent, username, isGreenScreen, skipConsent }: InitialValues) => {
        // reset
        this.footageFile = null;
        this.idleVideo = undefined;
        this.footageUploadStatus = RequestStatus.IDLE;
        this.consentFile = null;
        this.consentFileSrc = '';
        this.uploadedConsentId = '';
        this.consentValidationStatus = RequestStatus.IDLE;
        this.tab = AvatarCreateTab.INSTRUCTIONS;
        this.consentValidationErrorMessage = '';
        this.resetConfirmations();
        this.footageMethod = null;
        this.usedFootageScript = false;
        this.footageScriptLanguage = '';
        this.consentLanguage = 'english';
        this.thumbnail_url = '';
        this.isConsentAgreed = false;

        // initialize
        this.id = id;
        this.footageUrl = footageUrl;
        this.selectedConsent = consent || null;
        this.username = username || '';
        this.isGreenScreen = isGreenScreen || false;
        this.initialValues = { id, footageUrl, consent, username, isGreenScreen };
        this.skipConsent = skipConsent ?? false;

        if (consent) {
            this.setConsentValidationStatus(RequestStatus.SUCCESS);
        }
        if (footageUrl) {
            this.confirmAllFootage();
        }
    };

    @action
    setTab = (tab: AvatarCreateTab) => {
        this.tab = tab;
    };

    @action
    setFootageUrl = (url: string | undefined) => {
        this.footageUrl = url;
    };

    @action
    setFootageFile = (file: File | null) => {
        this.footageFile = file;
    };

    @action
    setFootageUploadStatus = (status: RequestStatus) => {
        this.footageUploadStatus = status;
    };

    @action
    toggleConfirmation = (index: number) => {
        const key = Object.keys(this.requirementsMap)[index] as FootageRequirement;
        this.requirementsMap[key].confirmed = !this.requirementsMap[key].confirmed;
    };

    @action
    confirmAllFootage = () => {
        Object.values(this.requirementsMap).forEach(requirement => {
            requirement.confirmed = true;
        });
    };

    @action
    resetConfirmations = () => {
        Object.values(this.requirementsMap).forEach(requirement => {
            requirement.confirmed = false;
        });
    };

    @action
    setConsentFileSrc = (file: string) => {
        this.consentFileSrc = file;
    };

    @action
    setConsentFile = (file: File | null) => {
        this.consentFile = file;
    };

    @action
    setConsentScript = (script: string) => {
        this.consentScript = script;
    };

    @action
    setUsername = (username: string) => {
        this.username = username;
    };

    @action
    setConsentValidationStatus = (status: RequestStatus) => {
        this.consentValidationStatus = status;
    };

    @action
    setConsentError = (message: string) => {
        if (message) {
            this.consentValidationStatus = RequestStatus.ERROR;
        }
        this.consentValidationErrorMessage = message;
    };

    @action
    setUpgradeMinutesPopupOpen = (should: boolean) => {
        this.upgradeMinutesPopupOpen = should;
    };

    @action
    updateSelectedConsent = (consent: Consent) => {
        this.selectedConsent = consent;

        if (consent.status === 'error') {
            this.setConsentValidationStatus(RequestStatus.ERROR);
            this.consentValidationErrorMessage = ErrorMessages[consent.error?.kind || ConsentValidationErrors.GeneralError];
            return;
        }

        this.setConsentValidationStatus(RequestStatus.SUCCESS);
    };

    @action
    clearFootage = () => {
        this.setFootageFile(null);
        this.setFootageUrl(undefined);
    };

    @action
    clearConsent = () => {
        this.selectedConsent = null;
        this.setConsentFile(null);
        this.setConsentFileSrc('');
        this.setConsentScript('');
        this.setConsentValidationStatus(RequestStatus.IDLE);
        this.consentValidationErrorMessage = '';
    };

    @action
    setUploadedConsentId = (id: string) => {
        this.uploadedConsentId = id;
    };

    @action
    setFootageMethod = (method: FootageMethod) => {
        this.footageMethod = method;
    };

    @action
    setUsedFootageScript = (value: boolean) => {
        this.usedFootageScript = value;
    };

    @action
    setFootageScriptLanguage = (language: string) => {
        this.footageScriptLanguage = language;
    };

    @action
    setConsentLanguage = (language: SupportedLanguage) => {
        this.consentLanguage = language;
    };

    @action
    setIsConsentAgreed = (value: boolean) => {
        this.isConsentAgreed = value;
    };

    @action
    setThumbnailUrl = (thumbnailUrl: string) => {
        this.thumbnail_url = thumbnailUrl;
    };

    @action
    handleFootage = async (file: File) => {
        this.footageAbortController?.abort();
        const abortController = new AbortController();
        this.footageAbortController = abortController;

        this.footageUploadTime = new Date();

        this.setFootageFile(file);
        await handleFootage(this, abortController.signal);
    };

    @action
    handleConsent = async () => {
        this.consentAbortController?.abort();
        const abortController = new AbortController();
        this.consentAbortController = abortController;

        await validateConsent(this);
    };

    @action
    resetFootageUpload = () => {
        this.setFootageUploadStatus(RequestStatus.IDLE);
        this.clearFootage();
        this.resetConfirmations();
        this.resetGreenScreen();
        this.isGreenScreen = false;
        this.footageAbortController?.abort();
    };

    @action
    resetConsentUpload = () => {
        this.setConsentValidationStatus(RequestStatus.IDLE);
        this.clearConsent();
        this.consentAbortController?.abort();
    };

    @action
    resetUploads = () => {
        this.resetFootageUpload();
        this.resetConsentUpload();
    };

    @action
    toggleIsGreenScreen = () => {
        this.isGreenScreen = !this.isGreenScreen;

        if (!this.isGreenScreen) {
            this.resetGreenScreen();
        }
    };

    @action
    setGreenScreen = (chroma_key: string, similarity: number, blend: number) => {
        this.greenscreen = { chroma_key, similarity, blend };
    };

    @action
    resetGreenScreen = () => {
        this.greenscreen = undefined;
    };

    @action
    setIdleVideo = (url: string | undefined) => {
        this.idleVideo = url;
    };
}

const premiumPlusCreateAvatarStore = new PremiumPlusCreateAvatarStore();

export default premiumPlusCreateAvatarStore;
